// Types des fichiers générés par tools/build_assets.py (voir docs/asset-format.md)
// + utilitaires de lecture des buffers binaires.

/** Entrée de layout : [offset en octets, nombre d'éléments, type]. */
export type LayoutEntry = [number, number, 'f32' | 'u32' | 'u16' | 'u8' | 'i16' | 'i32'];
export type Layout = Record<string, LayoutEntry>;

export interface BaseMeta {
  version: number;
  vertexCount: number;
  renderVertexCount: number;
  triangleCount: number;
  quadCount: number;
  file: string;
  layout: Layout;
  groups: Record<string, { faces: number; verts: number; vmin: number; vmax: number }>;
  skeleton: string;
  weights: { file: string; layout: Layout };
  boneCount: number;
}

export interface BoneDef {
  name: string;
  parent: number;          // index dans bones, -1 = racine
  head: number;            // index de joint
  tail: number;
  planes: [number, number, number][]; // plans (3 joints) dont la normale moyenne donne l'axe X (roll)
}

export interface SkeletonDef {
  name: string;
  bones: BoneDef[];
  joints: number[][];      // joint -> sommets de base dont la moyenne donne la position
  jointNames: string[];
}

/** targets/index.json : nom -> [fichier, offset, nb sommets, échelle]. */
export interface TargetIndex {
  version: number;
  files: string[];
  targets: Record<string, [number, number, number, number]>;
  runtimeMorphs: string[];
}

export interface ModifierDef {
  name: string;            // ex. "nose/nose-trans-down|up"
  group: string;
  min: number;             // -1 (paire gauche/droite) ou 0
  max: number;
  left: string[];          // cibles appliquées pour les valeurs < 0 (poids = -v)
  right: string[];         // cibles appliquées pour les valeurs > 0 (poids = v)
  desc?: string;
}

export interface ModifiersFile {
  version: number;
  categories: [string, string[]][];
  macroGroups: Record<string, string[]>;
  /** [cible, variables] : poids = produit des valeurs des variables (femaleVal, youngVal, …). */
  macroTargets: [string, string[]][];
  modifiers: ModifierDef[];
  sliders: Record<string, Record<string, { mod: string; label?: string }[]>>;
}

export interface MaterialDef {
  name: string | null;
  diffuseColor: [number, number, number];
  specularColor: [number, number, number];
  shininess: number;
  opacity: number;
  translucency: number;
  transparent: boolean;
  alphaToCoverage: boolean;
  backfaceCull: boolean;
  castShadows: boolean;
  receiveShadows: boolean;
  textures: Partial<Record<'diffuse' | 'normal' | 'bump' | 'specular' | 'transparency' | 'ao' | 'displacement', string>>;
  extra: Record<string, string | number | boolean | number[]>;
}

export interface ProxyAsset {
  id: string;
  kind: 'proxy';
  type: string;            // eyes | eyebrows | eyelashes | teeth | tongue | hair | clothes | proxymeshes
  name: string;
  uuid: string | null;
  tags: string[];
  zDepth: number;
  /** x/y/z : [sommet1, sommet2, distance de référence (m)] -> échelle des offsets. */
  scale: [number, number, number][] | null;
  counts: { verts: number; renderVerts: number; tris: number; faces: number; deleteVerts: number };
  geometry: string;
  layout: Layout;
  material: MaterialDef | null;
}

export interface MaterialAsset {
  id: string;
  kind: 'skin' | 'eyeMaterial';
  name: string;
  material: MaterialDef;
}

export type TypedArray = Float32Array | Uint32Array | Uint16Array | Uint8Array | Int16Array | Int32Array;

/** Vue typée (sans copie) sur un tableau d'un buffer binaire généré. */
export function view(buf: ArrayBuffer, e: LayoutEntry): TypedArray {
  const [off, n, t] = e;
  switch (t) {
    case 'f32': return new Float32Array(buf, off, n);
    case 'u32': return new Uint32Array(buf, off, n);
    case 'u16': return new Uint16Array(buf, off, n);
    case 'u8': return new Uint8Array(buf, off, n);
    case 'i16': return new Int16Array(buf, off, n);
    case 'i32': return new Int32Array(buf, off, n);
  }
}

export function viewOf<T extends TypedArray>(buf: ArrayBuffer, layout: Layout, name: string): T {
  const e = layout[name];
  if (!e) throw new Error(`human: tableau "${name}" absent du buffer`);
  return view(buf, e) as T;
}

/** Index (Uint16 | Uint32) quel que soit le type stocké. */
export type IndexArray = Uint16Array | Uint32Array;
